/**
 * ImportClientsWizard — Multi-step wizard for bulk client import.
 *
 * Steps:
 *  1. Upload — Select a file (xlsx, csv, json, etc.) + download template
 *  2. Parsing — AI parses & classifies (PubSub progress)
 *  3. Preview — Review & edit parsed rows (type, lawyers, etc.)
 *  4. Confirm — Execute client creation with progress
 *  5. Documents — Optional ZIP/file upload for client documents
 *  6. Results — Summary
 */

import React, { useState, useCallback, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import {
  PiUploadLight,
  PiSpinnerGapLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiXLight,
  PiPencilLight,
  PiTrashLight,
  PiDownloadLight,
  PiUsersFourLight,
  PiFilesLight,
  PiArrowRightLight,
  PiFolderOpenLight,
  PiFolderLight,
  PiFileLight,
} from 'react-icons/pi';

import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import { useImportPubSub } from '../../hooks/useImportPubSub';
import {
  uploadClientsImport,
  getImportStatus,
  updateImportPreview,
  confirmImport,
  uploadImportDocuments,
  skipDocumentUpload,
  downloadClientTemplate,
} from '../../services/import/importApi';
import type {
  ImportWizardStep,
  ImportClientRow,
  ImportPreviewData,
  ImportConfirmResponse,
  ImportDocumentsResponse,
} from '../../types/import';

interface ImportClientsWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const STEPS: { key: ImportWizardStep; label: string }[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'parsing', label: 'AI Parsing' },
  { key: 'preview', label: 'Preview' },
  { key: 'confirm', label: 'Creating' },
  { key: 'documents', label: 'Documents' },
  { key: 'results', label: 'Results' },
];

const ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv', 'json', 'txt', 'pdf', 'docx', 'md'];

export default function ImportClientsWizard({
  isOpen,
  onClose,
  onComplete,
}: ImportClientsWizardProps) {
  const { t } = useTranslation();

  // Wizard state
  const [step, setStep] = useState<ImportWizardStep>('upload');
  const [jobUuid, setJobUuid] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData | null>(null);
  const [confirmResult, setConfirmResult] = useState<ImportConfirmResponse | null>(null);
  const [docsResult, setDocsResult] = useState<ImportDocumentsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [docFiles, setDocFiles] = useState<File[]>([]);

  // PubSub hook
  const { progress, reset: resetProgress } = useImportPubSub({
    correlationId,
    enabled: step === 'parsing',
    onPreviewReady: async (uuid) => {
      if (uuid || jobUuid) {
        await loadPreview(uuid || jobUuid!);
        setStep('preview');
      }
    },
    onFailed: (err) => {
      setError(err);
      setStep('upload');
    },
  });

  // Reset on open/close
  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setJobUuid(null);
      setCorrelationId(null);
      setSelectedFile(null);
      setPreviewData(null);
      setConfirmResult(null);
      setDocsResult(null);
      setIsLoading(false);
      setError(null);
      setEditingRow(null);
      setDocFiles([]);
      resetProgress();
    }
  }, [isOpen, resetProgress]);

  // ── Upload ──
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await uploadClientsImport(selectedFile);
      if (response.success && response.job_uuid) {
        setJobUuid(response.job_uuid);
        setCorrelationId(response.correlation_id || null);
        setStep('parsing');
      } else {
        const e = response.error;
        setError(typeof e === 'string' ? e : typeof e === 'object' && e ? (e.description || e.title || 'Upload failed') : 'Upload failed');
      }
    } catch (err: any) {
      const errorData = err.response?.data?.error;
      const errorMsg = typeof errorData === 'string' ? errorData : errorData?.description || errorData?.title || err.message || 'Upload failed';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile]);

  // ── Preview ──
  const loadPreview = useCallback(async (uuid: string) => {
    try {
      const response = await getImportStatus(uuid);
      if (response.success && response.preview) {
        const rows = response.preview.rows as ImportClientRow[];
        rows.forEach((row) => {
          row._selected = !row.is_duplicate;
        });
        setPreviewData({ ...response.preview, rows });
      }
    } catch {
      setError('Failed to load preview');
    }
  }, []);

  // ── Preview editing ──
  const rows = previewData?.rows as ImportClientRow[] | undefined;
  const selectedCount = rows?.filter((r) => r._selected).length || 0;
  const duplicateCount = rows?.filter((r) => r.is_duplicate).length || 0;
  const warningCount = rows?.filter((r) => r.unmatched_lawyers && r.unmatched_lawyers.length > 0).length || 0;

  const toggleRow = useCallback(
    (idx: number) => {
      if (!previewData) return;
      const r = [...previewData.rows] as ImportClientRow[];
      r[idx] = { ...r[idx], _selected: !r[idx]._selected };
      setPreviewData({ ...previewData, rows: r });
    },
    [previewData]
  );

  const toggleAll = useCallback(
    (selected: boolean) => {
      if (!previewData) return;
      const r = (previewData.rows as ImportClientRow[]).map((row) => ({
        ...row,
        _selected: row.is_duplicate ? false : selected,
      }));
      setPreviewData({ ...previewData, rows: r });
    },
    [previewData]
  );

  const updateRow = useCallback(
    (idx: number, updates: Partial<ImportClientRow>) => {
      if (!previewData) return;
      const r = [...previewData.rows] as ImportClientRow[];
      r[idx] = { ...r[idx], ...updates, _edited: true };
      setPreviewData({ ...previewData, rows: r });
    },
    [previewData]
  );

  const removeRow = useCallback(
    (idx: number) => {
      if (!previewData) return;
      const r = [...previewData.rows] as ImportClientRow[];
      r.splice(idx, 1);
      setPreviewData({ ...previewData, rows: r });
    },
    [previewData]
  );

  // ── Confirm ──
  const handleConfirm = useCallback(async () => {
    if (!jobUuid || !previewData) return;
    const selectedRows = (previewData.rows as ImportClientRow[]).filter((r) => r._selected);
    if (selectedRows.length === 0) {
      setError('No rows selected');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await updateImportPreview(jobUuid, selectedRows, { total_rows: selectedRows.length });
      setStep('confirm');
      const result = await confirmImport(jobUuid);
      setConfirmResult(result);

      // If clients were created, go to documents step
      if (result.created_count > 0) {
        setStep('documents');
      } else {
        setStep('results');
      }
    } catch (err: any) {
      const errorData = err.response?.data?.error;
      const errorMsg = typeof errorData === 'string' ? errorData : errorData?.description || errorData?.title || err.message || 'Import failed';
      setError(errorMsg);
      setStep('preview');
    } finally {
      setIsLoading(false);
    }
  }, [jobUuid, previewData]);

  // ── Documents upload ──
  const [docMapping, setDocMapping] = useState<Record<string, string>>({});

  const handleDocFilesChange = useCallback((files: File[]) => {
    // Extract client folder mapping from webkitRelativePath if available (folder uploads)
    const mapping: Record<string, string> = {};
    for (const file of files) {
      const relPath = (file as any).webkitRelativePath || '';
      if (relPath) {
        const parts = relPath.split('/').filter(Boolean);
        // parts: [RootFolder, ClientName, subfolder?, filename]
        if (parts.length >= 2) {
          const clientFolder = parts.length >= 3 ? parts[1] : parts[0];
          mapping[file.name] = clientFolder;
        }
      }
    }
    setDocFiles(files);
    setDocMapping(Object.keys(mapping).length > 0 ? mapping : {});
  }, []);

  const handleDocUpload = useCallback(async () => {
    if (!jobUuid || docFiles.length === 0) return;
    setIsLoading(true);
    setError(null);
    try {
      const isZip = docFiles.length === 1 && docFiles[0].name.endsWith('.zip');
      const mappingToSend = Object.keys(docMapping).length > 0 ? docMapping : undefined;
      const result = await uploadImportDocuments(jobUuid, docFiles, mappingToSend, isZip);
      // Filter out macOS junk from failure list (.__MACOSX, ._files, .DS_Store)
      if (result.failed && result.failed.length > 0) {
        const isMacJunk = (f: string) =>
          f.includes('__MACOSX') || f.includes('.DS_Store') || f.split('/').some((p) => p.startsWith('._'));
        const realFailures = result.failed.filter((item: any) => !isMacJunk(item.file || ''));
        result.failed = realFailures;
        result.failed_count = realFailures.length;
      }
      setDocsResult(result);
      setStep('results');
    } catch (err: any) {
      const errorData = err.response?.data?.error;
      const errorMsg = typeof errorData === 'string' ? errorData : errorData?.description || errorData?.title || err.message || 'Document upload failed';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [jobUuid, docFiles, docMapping]);

  const handleSkipDocuments = useCallback(async () => {
    if (!jobUuid) return;
    try {
      await skipDocumentUpload(jobUuid);
    } catch {
      /* ignore */
    }
    setStep('results');
  }, [jobUuid]);

  const handleClose = useCallback(() => {
    if (confirmResult && confirmResult.created_count > 0) {
      onComplete?.();
    }
    onClose();
  }, [confirmResult, onComplete, onClose]);

  // ── Render ──
  const currentStepIndex = STEPS.findIndex((s) => s.key === step);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <PiUsersFourLight className="w-6 h-6 text-blue-600" />
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('Import Clients')}
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <PiXLight className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Step indicator */}
                <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-2">
                    {STEPS.map((s, i) => (
                      <div key={s.key} className="flex items-center">
                        <div
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            i === currentStepIndex
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                              : i < currentStepIndex
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                              : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                          }`}
                        >
                          {i < currentStepIndex && <PiCheckCircleLight className="w-3.5 h-3.5" />}
                          {t(s.label)}
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className="w-4 h-px bg-gray-200 dark:bg-gray-700 mx-0.5" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-6 min-h-[400px] max-h-[60vh] overflow-y-auto">
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                      <PiWarningCircleLight className="w-5 h-5 flex-shrink-0" />
                      <span>{error}</span>
                      <button onClick={() => setError(null)} className="ml-auto">
                        <PiXLight className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Step: Upload */}
                  {step === 'upload' && (
                    <div className="flex flex-col items-center gap-6">
                      <div className="text-center">
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">
                          {t('Upload a file with client data')}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {t(
                            'Upload an Excel, CSV, JSON, or any text file with your client list. AI will parse and classify clients automatically.'
                          )}
                        </p>
                      </div>

                      {/* Template download */}
                      <button
                        onClick={() => downloadClientTemplate()}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        <PiDownloadLight className="w-4 h-4" />
                        {t('Download Excel Template')}
                      </button>

                      {/* FileUploader for client data file */}
                      <div className="w-full max-w-md">
                        <FileUploader
                          files={selectedFile ? [selectedFile] : []}
                          onFilesChange={(files) => {
                            setSelectedFile(files[0] || null);
                            setError(null);
                          }}
                          allowedExtensions={ALLOWED_EXTENSIONS}
                          maxFiles={1}
                        />
                      </div>

                      <Button
                        variant="primary"
                        size="small"
                        onClick={handleUpload}
                        disabled={!selectedFile || isLoading}
                        isLoading={isLoading}
                      >
                        {t('Upload & Parse')}
                      </Button>
                    </div>
                  )}

                  {/* Step: Parsing */}
                  {step === 'parsing' && (
                    <div className="flex flex-col items-center gap-4 py-12">
                      <PiSpinnerGapLight className="w-12 h-12 text-blue-500 animate-spin" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white">
                        {t('Parsing and classifying your clients...')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {progress.message || t('This usually takes 15-45 seconds')}
                      </p>
                    </div>
                  )}

                  {/* Step: Preview */}
                  {step === 'preview' && rows && (
                    <div className="space-y-4">
                      {/* Summary */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-700 dark:text-gray-300">
                            {t('Total')}: <strong>{rows.length}</strong>
                          </span>
                          <span className="text-green-600">
                            {t('Selected')}: <strong>{selectedCount}</strong>
                          </span>
                          {duplicateCount > 0 && (
                            <span className="text-amber-600">
                              {t('Duplicates')}: <strong>{duplicateCount}</strong>
                            </span>
                          )}
                          {warningCount > 0 && (
                            <span className="text-orange-600">
                              {t('Unmatched lawyers')}: <strong>{warningCount}</strong>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => toggleAll(true)} className="text-xs text-blue-600 hover:underline">
                            {t('Select All')}
                          </button>
                          <span className="text-gray-300">|</span>
                          <button onClick={() => toggleAll(false)} className="text-xs text-gray-500 hover:underline">
                            {t('Deselect All')}
                          </button>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-2 py-2 w-10">
                                <input
                                  type="checkbox"
                                  checked={selectedCount === rows.filter((r) => !r.is_duplicate).length}
                                  onChange={(e) => toggleAll(e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {t('Name')}
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {t('Type')}
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {t('Email')}
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {t('Phone')}
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {t('Lawyers')}
                              </th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">
                                {t('Status')}
                              </th>
                              <th className="px-2 py-2 w-16" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {rows.map((row, idx) => (
                              <tr
                                key={idx}
                                className={`${
                                  row.is_duplicate
                                    ? 'bg-amber-50/50 dark:bg-amber-900/10'
                                    : row._selected
                                    ? 'bg-white dark:bg-gray-900'
                                    : 'bg-gray-50/50 dark:bg-gray-800/50 opacity-60'
                                } hover:bg-gray-50 dark:hover:bg-gray-800/70`}
                              >
                                <td className="px-2 py-2">
                                  <input
                                    type="checkbox"
                                    checked={!!row._selected}
                                    disabled={!!row.is_duplicate}
                                    onChange={() => toggleRow(idx)}
                                    className="rounded border-gray-300"
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  {editingRow === idx ? (
                                    <Input
                                      type="text"
                                      value={row.name}
                                      onChange={(e) => updateRow(idx, { name: e.target.value })}
                                      className="!py-1 !text-xs"
                                    />
                                  ) : (
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                      {row.name}
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-2">
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
                                    row.is_individual
                                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                      : 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                  }`}>
                                    {row.client_type_name || row.client_type_code?.replace(/_/g, ' ') || '—'}
                                  </span>
                                </td>
                                <td className="px-2 py-2">
                                  {editingRow === idx ? (
                                    <Input
                                      type="email"
                                      value={row.contact_email}
                                      onChange={(e) => updateRow(idx, { contact_email: e.target.value })}
                                      className="!py-1 !text-xs"
                                    />
                                  ) : (
                                    <span className="text-gray-700 dark:text-gray-300 text-xs">
                                      {row.contact_email}
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-2 text-xs text-gray-500">
                                  {row.contact_phone || '—'}
                                </td>
                                <td className="px-2 py-2">
                                  {row.matched_lawyers && row.matched_lawyers.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {row.matched_lawyers.map((l, i) => (
                                        <span
                                          key={i}
                                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                        >
                                          {l.name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : null}
                                  {row.unmatched_lawyers && row.unmatched_lawyers.length > 0 ? (
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                      {row.unmatched_lawyers.map((name, i) => (
                                        <span
                                          key={i}
                                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                                          title={t('Unmatched — assign after import')}
                                        >
                                          <PiWarningCircleLight className="w-3 h-3" /> {name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : null}
                                  {(!row.matched_lawyers || row.matched_lawyers.length === 0) &&
                                    (!row.unmatched_lawyers || row.unmatched_lawyers.length === 0) && (
                                      <span className="text-xs text-gray-400">—</span>
                                    )}
                                </td>
                                <td className="px-2 py-2">
                                  {row.is_duplicate ? (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                      {t('Dup')}
                                    </span>
                                  ) : row.warning ? (
                                    <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700" title={row.warning}>
                                      <PiWarningCircleLight className="w-3.5 h-3.5" />
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                      <PiCheckCircleLight className="w-3.5 h-3.5" />
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-2">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => setEditingRow(editingRow === idx ? null : idx)}
                                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
                                    >
                                      <PiPencilLight className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => removeRow(idx)}
                                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                                    >
                                      <PiTrashLight className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Step: Confirm (processing) */}
                  {step === 'confirm' && (
                    <div className="flex flex-col items-center gap-4 py-12">
                      <PiSpinnerGapLight className="w-12 h-12 text-blue-500 animate-spin" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white">
                        {t('Creating clients & assigning lawyers...')}
                      </h3>
                      <p className="text-sm text-gray-500">{t('Please do not close this window.')}</p>
                    </div>
                  )}

                  {/* Step: Documents */}
                  {step === 'documents' && (
                    <div className="flex flex-col items-center gap-6">
                      <div className="text-center">
                        <PiFilesLight className="w-12 h-12 mx-auto text-blue-500 mb-3" />
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">
                          {t('Upload Client Documents')} <span className="text-sm text-gray-400">({t('Optional')})</span>
                        </h3>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
                          {t(
                            'Attach documents to the clients you just imported. You can upload a single ZIP file, or use the folder picker to select a folder.'
                          )}
                        </p>
                      </div>

                      {/* Expected folder structure */}
                      <div className="w-full max-w-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <PiFolderOpenLight className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('Expected folder structure')}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono space-y-1 ml-2">
                          <p className="flex items-center gap-1"><PiFolderLight className="w-3.5 h-3.5 text-blue-500" /> ClientDocuments/</p>
                          <p className="flex items-center gap-1 ml-4"><PiFolderLight className="w-3.5 h-3.5 text-blue-400" /> Ola Nordmann/</p>
                          <p className="flex items-center gap-1 ml-8"><PiFileLight className="w-3.5 h-3.5 text-gray-400" /> contract.pdf</p>
                          <p className="flex items-center gap-1 ml-8"><PiFileLight className="w-3.5 h-3.5 text-gray-400" /> id_document.pdf</p>
                          <p className="flex items-center gap-1 ml-4"><PiFolderLight className="w-3.5 h-3.5 text-blue-400" /> Acme AS/</p>
                          <p className="flex items-center gap-1 ml-8"><PiFileLight className="w-3.5 h-3.5 text-gray-400" /> agreement.docx</p>
                          <p className="flex items-center gap-1 ml-8"><PiFileLight className="w-3.5 h-3.5 text-gray-400" /> invoice.xlsx</p>
                        </div>
                        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                          {t('Each subfolder name must match the client name exactly. Files inside will be attached to the matching client.')}
                        </p>
                      </div>

                      {confirmResult && (
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                          <PiCheckCircleLight className="w-4 h-4 flex-shrink-0" />
                          {t('{{count}} clients created successfully', { count: confirmResult.created_count })}
                        </div>
                      )}

                      {/* FileUploader for documents */}
                      <div className="w-full max-w-lg">
                        <FileUploader
                          files={docFiles}
                          onFilesChange={handleDocFilesChange}
                          allowedExtensions={['zip', 'pdf', 'doc', 'docx', 'txt', 'xlsx', 'xls', 'csv', 'msg', 'ppt', 'pptx']}
                        />
                      </div>

                      {docFiles.length > 0 && Object.keys(docMapping).length > 0 && (
                        <div className="w-full max-w-lg p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                          <PiFolderOpenLight className="w-4 h-4 inline mr-1" />
                          {t('Folder structure detected — documents will be matched to clients by folder name.')}
                        </div>
                      )}

                      <div className="flex items-center gap-3">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleDocUpload}
                          disabled={docFiles.length === 0 || isLoading}
                          isLoading={isLoading}
                          icon={<PiUploadLight className="w-4 h-4" />}
                        >
                          {t('Upload Documents')}
                        </Button>
                        <Button variant="secondary" size="small" onClick={handleSkipDocuments}>
                          {t('Skip')}
                          <PiArrowRightLight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step: Results */}
                  {step === 'results' && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-6 p-4 rounded-xl bg-gray-50 dark:bg-gray-800">
                        {(confirmResult?.created_count || 0) > 0 ? (
                          <PiCheckCircleLight className="w-10 h-10 text-green-500" />
                        ) : (
                          <PiWarningCircleLight className="w-10 h-10 text-red-500" />
                        )}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {(confirmResult?.created_count || 0) > 0 ? t('Import Completed') : t('Import Failed')}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t('{{created}} of {{total}} clients imported', {
                              created: confirmResult?.created_count || 0,
                              total: (confirmResult?.created_count || 0) + (confirmResult?.failed_count || 0),
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
                          <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                            {confirmResult?.created_count || 0}
                          </div>
                          <div className="text-sm text-green-600">{t('Clients Created')}</div>
                        </div>
                        <div className="p-4 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
                          <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                            {confirmResult?.failed_count || 0}
                          </div>
                          <div className="text-sm text-red-600">{t('Failed')}</div>
                        </div>
                        {docsResult && (
                          <div className="p-4 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
                            <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                              {docsResult.uploaded}
                            </div>
                            <div className="text-sm text-blue-600">{t('Documents Uploaded')}</div>
                            {docsResult.failed_count > 0 && (
                              <div className="text-xs text-blue-500 mt-1">
                                {docsResult.failed_count} {t('failed')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Document upload failures */}
                      {docsResult && docsResult.failed && docsResult.failed.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('Document Upload Failures')}
                          </h4>
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {docsResult.failed.slice(0, 10).map((item: any, idx: number) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 px-3 py-1.5 text-xs bg-orange-50 dark:bg-orange-900/10 rounded-lg"
                              >
                                <PiWarningCircleLight className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                <span className="text-gray-600 dark:text-gray-400 truncate">
                                  {item.file}: {item.error}
                                </span>
                              </div>
                            ))}
                            {docsResult.failed.length > 10 && (
                              <p className="text-xs text-gray-400 px-3">
                                {t('...and {{count}} more', { count: docsResult.failed.length - 10 })}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {confirmResult?.failed_items && confirmResult.failed_items.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('Failed Items')}
                          </h4>
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {confirmResult.failed_items.map((item, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 px-3 py-1.5 text-xs bg-red-50 dark:bg-red-900/10 rounded-lg"
                              >
                                <PiWarningCircleLight className="w-4 h-4 text-red-500 flex-shrink-0" />
                                <span className="text-gray-600 dark:text-gray-400">
                                  Row {item.row + 1}
                                  {item.name && ` (${item.name})`}: {item.error}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <div>
                    {step === 'preview' && (
                      <span className="text-sm text-gray-500">
                        {selectedCount} {t('clients will be created')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {(step === 'upload' || step === 'results') && (
                      <Button variant="secondary" size="small" onClick={handleClose}>
                        {step === 'results' ? t('Done') : t('Cancel')}
                      </Button>
                    )}
                    {step === 'preview' && (
                      <>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => {
                            setStep('upload');
                            setPreviewData(null);
                          }}
                        >
                          {t('Back')}
                        </Button>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleConfirm}
                          disabled={selectedCount === 0 || isLoading}
                          isLoading={isLoading}
                        >
                          {t('Import {{count}} Clients', { count: selectedCount })}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
